import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { getIsBitSet, ListViewBitFlagsMasks } from 'mini-bit-flags/lib/utils/listViewBitFlagsUtil';
import { SubstrateEntityType } from '../schema/SubstrateEntityType';
import { SubstratePeopleSubtype, SubstratePeopleType } from '../schema/SubstratePeopleTypes';
import {
    TermFilter,
    OrFilter,
    AndFilter,
    SubstrateEntityFilter,
    RangeFilterExpression,
    RangeFilter,
} from '../schema/SubstrateEntityFilter';

/**
 * Returns an object of type TermFilter formed from the given params
 * @param filterTermType
 * @param value
 */
export const createTermFilter = (filterTermType: string, value: string): TermFilter => ({
    Term: {
        [filterTermType]: value,
    },
});

/**
 * Returns an object of type OrFilter from the given array of filters
 * @param filters
 */
export const createOrFilter = (filters: SubstrateEntityFilter[]): OrFilter => ({
    Or: filters,
});

/**
 * Returns an object of type AndFilter from the given array of filters
 * @param filters
 */
export const createAndFilter = (filters: SubstrateEntityFilter[]): AndFilter => ({
    And: filters,
});

/**
 * Returns an object of type RangeFilter for the given key;
 */
export const createRangeFilter = (
    rangeFilterKey: string,
    rangeValue: RangeFilterExpression
): RangeFilter => ({
    Range: {
        [rangeFilterKey]: rangeValue,
    },
});

/**
 * TODO: 91513 : Move the people entity filter code to mini-3s-people-searchstore
 * Returns the correct SubstrateEntityFilter object for the provided entityType
 * @param entityType
 * @param currentFolderScopeId
 */
export const createFilterForEntity = (
    entityType: SubstrateEntityType,
    currentFolderScopeId?: string,
    shouldIncludeRoom?: boolean
): SubstrateEntityFilter => {
    switch (entityType) {
        case SubstrateEntityType.Conversation:
        case SubstrateEntityType.Message:
            return createMailFilter(currentFolderScopeId);
        case SubstrateEntityType.People:
            return createPeopleFilter(shouldIncludeRoom);
        default:
            return null;
    }
};

const createMailFilter = (currentFolderScopeId?: string): SubstrateEntityFilter => {
    const currentFolderScopeFilter =
        folderIdToName(currentFolderScopeId) === 'msgfolderroot'
            ? createTermFilter('DistinguishedFolderName', 'msgfolderroot')
            : createTermFilter('FolderId', currentFolderScopeId);
    return getIsBitSet(ListViewBitFlagsMasks.ExcludeDeletedItemsInSearch)
        ? currentFolderScopeFilter
        : createOrFilter([
              currentFolderScopeFilter,
              createTermFilter('DistinguishedFolderName', 'DeletedItems'),
          ]);
};

const createPeopleFilter = (shouldIncludeRoom: boolean = false) => {
    const allowedPeopleTypes = [
        createTermFilter('PeopleType', SubstratePeopleType.Person),
        createTermFilter('PeopleType', SubstratePeopleType.Group),
    ];

    const allowedPeopleSubTypes = [
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.OrganizationUser),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.OrganizationContact),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.PersonalContact),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.ImplicitContact),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.ChatImplicitContact),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.UnifiedGroup),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.PublicDistributionList),
        createTermFilter('PeopleSubType', SubstratePeopleSubtype.PersonalDistributionList),
    ];

    if (shouldIncludeRoom) {
        allowedPeopleTypes.push(createTermFilter('PeopleType', SubstratePeopleType.Other));
        allowedPeopleSubTypes.push(createTermFilter('PeopleSubType', SubstratePeopleSubtype.Room));
    }

    return createAndFilter([
        createOrFilter(allowedPeopleTypes),
        createOrFilter(allowedPeopleSubTypes),
    ]);
};
